import { useEffect, useState } from 'react';
import { CircleCheckBigIcon, RadioIcon } from 'lucide-react';

import { JsonBlock } from '@/components/JsonBlock';
import { Badge } from '@/components/ui/badge';
import { formatLatency, formatRelative } from '@/lib/format';
import type { Onboarding } from '@/api/schemas';
import { cn } from '@/lib/utils';

/**
 * The panel that confirms the first event. This is the product's first moment of proof, and
 * plan Phase 3 makes it the milestone that Slice Zero is measured by.
 *
 * Three things it must do, from FR-DASH-8 and acceptance A3.5:
 *
 *   1. Poll every two seconds while waiting, with no spinner that could
 *      outlive the wait.
 *   2. Show the raw JSON of the customer's actual first event, not a
 *      reassuring summary of it.
 *   3. Show the receive latency in milliseconds, because the number is the
 *      claim: this pipeline is fast, and here is the measurement.
 *
 * The elapsed counter is deliberate. A wait with no visible progress feels
 * broken after about fifteen seconds; a wait with a running clock feels like
 * something is happening, and it makes the ten minute escape hatch legible
 * rather than sudden.
 */
export function WaitingForFirstEvent({
  onboarding,
  waitingSince,
}: {
  onboarding: Onboarding | undefined;
  waitingSince: number;
}) {
  const received = onboarding?.first_event_at != null;

  if (received && onboarding) {
    return <FirstEventReceived onboarding={onboarding} />;
  }

  return <StillWaiting waitingSince={waitingSince} />;
}

function StillWaiting({ waitingSince }: { waitingSince: number }) {
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - waitingSince) / 1000));

  useEffect(() => {
    const id = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - waitingSince) / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [waitingSince]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-[40px] border border-border bg-card p-6"
    >
      <div className="flex items-start gap-3">
        <span className="relative mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-accent">
          <span className="absolute inset-0 rounded-full animate-(--animate-pulse-ring)" aria-hidden />
          <RadioIcon className="size-4 text-primary" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Waiting for your first event</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            This panel is polling every two seconds. Load the page you installed the snippet on
            and your first event appears here.
          </p>
          <p className="zs-numeric mt-2 text-xs text-muted-foreground">
            Listening for {elapsed}s
          </p>
        </div>
      </div>
    </div>
  );
}

function FirstEventReceived({ onboarding }: { onboarding: Onboarding }) {
  // The live region announces once, when this element first replaces the
  // waiting panel. Polling continues underneath but the DOM here stops
  // changing, so a screen reader is not told about the same event every two
  // seconds.
  const firstEventAt = onboarding.first_event_at;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'rounded-[40px] border p-6 animate-(--animate-first-event)',
        'border-success/40 bg-success/10',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-success/20">
          <CircleCheckBigIcon className="size-4 text-success" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">First event received</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Your pipeline is live. Everything below is the event exactly as Zeligate stored it.
          </p>

          <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <dt className="text-xs text-muted-foreground">Receive latency</dt>
              <dd className="zs-numeric text-lg font-semibold">
                {onboarding.latency_ms === null
                  ? 'not measured'
                  : formatLatency(onboarding.latency_ms)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Arrived</dt>
              <dd className="text-lg font-semibold">
                {firstEventAt === null ? 'unknown' : formatRelative(firstEventAt)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Events so far</dt>
              <dd className="zs-numeric text-lg font-semibold">{onboarding.event_count}</dd>
            </div>
          </dl>
        </div>

        <Badge variant="success" className="mt-1">
          Live
        </Badge>
      </div>

      {onboarding.first_event_json === null ? (
        <p className="mt-4 text-xs text-muted-foreground">
          The event arrived, but its stored copy has already aged out of the onboarding cache.
          The live feed shows current events.
        </p>
      ) : (
        <JsonBlock
          className="mt-4"
          value={onboarding.first_event_json}
          label="Raw JSON of the first event received"
          maxHeight="18rem"
        />
      )}
    </div>
  );
}
